import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Tabs,
  Tab,
  Autocomplete,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  NavigateBefore,
  NavigateNext,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import projectsService from '../services/projectsService';
import timesheetService from '../services/timesheetService';
import timeOffService from '../services/timeOffService';
import usersService from '../services/usersService';
import { Project, WorkPackage, WorkUnit, TimesheetData, SaveTimesheetEntry, TimesheetEntry, TimesheetSummary, PublicHoliday, TimeOffRequest, TimesheetValidationData, TimesheetEntryValidation, LateUser, User, Client } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import TimesheetReportingPage from './TimesheetReportingPage';
import CustomCategoriesTab from '../components/timesheet/CustomCategoriesTab';
import timesheetCategoriesService from '../services/timesheetCategoriesService';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';
import { formatTimesheetCodeTitleLabel } from '../utils/timesheetLabels';
import { useTabSearchParam } from '../hooks/useTabSearchParam';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface TimesheetRow {
  id: string;
  project_id: number;
  work_package_id?: number;
  work_unit_id?: number;
  work_package_code?: string; // for absence rows - comes directly from backend
  work_unit_code?: string; // for absence rows - comes directly from backend
  hours: { [key: string]: number }; // date -> hours
  validated: { [key: string]: boolean }; // date -> validated status
  submitted_for_validation: { [key: string]: boolean }; // date -> submitted status
  isPrefilled?: boolean; // indicates if this row was pre-filled from previous week
  modifiedFields?: { [key: string]: boolean }; // date -> modified status
  isAbsence?: boolean; // indicates if this is an auto-generated absence row
  description?: string; // description for absence entries
}

interface SubmittedHourEdit {
  entryId?: number;
  date: string;
  project_id: number;
  work_package_id?: number | null;
  work_unit_id?: number | null;
  hours: number;
}

const TimesheetPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Nautilus Console Design System tokens
  const isDark = theme.palette.mode === 'dark';
  const NAUTILUS = { deepOcean: '#0f2134', sand: '#ccba9b', abyssal: '#0a1622' };
  const panelBorder = isDark ? '#2c3e50' : '#e0e0e0';
  const surfaceBg = isDark ? NAUTILUS.deepOcean : '#ffffff';

  // Check if user has PM or admin role
  const isPMOrAdmin = hasCapability(user, CAP.MANAGE_OTHERS_TIMESHEETS);
  const canManageCategories = hasCapability(user, CAP.TIMESHEET_CUSTOM_CATEGORIES);

  type TimesheetTabKey = 'mine' | 'delays' | 'submitted' | 'reporting' | 'categories';
  const tabs = useMemo(() => {
    const list: { key: TimesheetTabKey; label: string }[] = [
      { key: 'mine', label: 'My Timesheet' },
    ];
    if (isPMOrAdmin) {
      list.push(
        { key: 'delays', label: 'TS Delays' },
        { key: 'submitted', label: 'Submitted TS' },
        { key: 'reporting', label: 'Reporting' },
      );
    }
    if (canManageCategories) {
      list.push({ key: 'categories', label: 'Custom Category' });
    }
    return list;
  }, [isPMOrAdmin, canManageCategories]);

  const allowedTabKeys = useMemo(() => tabs.map((t) => t.key), [tabs]);
  const [activeTabKey, setActiveTabKey] = useTabSearchParam(allowedTabKeys, 'mine');
  const activeTabIndex = Math.max(0, tabs.findIndex((t) => t.key === activeTabKey));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isPrefilled, setIsPrefilled] = useState(false);
  const [prefilledFromPreviousWeek, setPrefilledFromPreviousWeek] = useState(false);

  // External User Selection for PMs
  const [externalUsers, setExternalUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  useEffect(() => {
    if (user) {
      // Default to current user
      if (selectedUserId === null) {
        setSelectedUserId(user.id);
      }

      if (isPMOrAdmin) {
        // Fetch external users
        usersService.getExternUsers()
          .then(users => {
            setExternalUsers(users);
          })
          .catch(err => console.error("Failed to fetch external users", err));
      }
    }
  }, [user, isPMOrAdmin]);

  // Handle user change
  const handleUserChange = (event: any) => {
    const newUserId = Number(event.target.value);
    setSelectedUserId(newUserId);
    // Reload data for the new user
    // Note: loadData will be triggered by useEffect dependency on selectedUserId (see below)
  };

  // Data
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [workPackages, setWorkPackages] = useState<{ [projectId: number]: WorkPackage[] }>({});
  const [workUnits, setWorkUnits] = useState<{ [projectId: number]: WorkUnit[] }>({});
  const [userPermissions, setUserPermissions] = useState<{
    work_packages: { [projectId: number]: number[] };
    work_units: { [projectId: number]: number[] };
  } | null>(null);
  const [rows, setRows] = useState<TimesheetRow[]>([]);
  const [summary, setSummary] = useState<TimesheetSummary | null>(null);
  const [publicHolidays, setPublicHolidays] = useState<PublicHoliday[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);

  // Tab management handled via activeTabKey above

  // Validation data (for PM/Admin)
  const [validationData, setValidationData] = useState<TimesheetValidationData | null>(null);
  const [lateUsers, setLateUsers] = useState<LateUser[]>([]);
  const [modifiedHours, setModifiedHours] = useState<{ [userId: number]: { [cellKey: string]: SubmittedHourEdit } }>({});

  // Submission Comment
  const [submitComment, setSubmitComment] = useState("");
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

  // Function to load work packages and units for a specific project if not already loaded
  const loadProjectDetails = async (projectId: number) => {
    if (projectId <= 0 || (workPackages[projectId] && workUnits[projectId])) {
      return;
    }

    // Custom categories already have their tree from /mine — do not overwrite via WP assignment APIs
    const project = projects.find((p) => p.id === projectId);
    if (project?.is_custom_category) {
      return;
    }

    try {
      const [wpData, wuData] = await Promise.all([
        projectsService.getWorkPackages(projectId),
        projectsService.getWorkUnits(projectId)
      ]);

      setWorkPackages(prev => ({ ...prev, [projectId]: wpData }));
      setWorkUnits(prev => ({ ...prev, [projectId]: wuData }));
    } catch (err) {
      console.error(`Failed to load details for project ${projectId}`, err);
    }
  };

  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(getWeekStart(new Date()));

  // Get Monday of current week
  function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  }

  // Get array of dates for the week
  function getWeekDates(weekStart: Date): Date[] {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      dates.push(date);
    }
    return dates;
  }

  const weekDates = getWeekDates(currentWeekStart);

  // Filter projects to show only active ones (today >= start_date and today <= end_date)
  // Custom categories bypass the date window (they have no project dates)
  const today = new Date().toISOString().split('T')[0];
  const filteredProjects = projects.filter(project => {
    if (project.is_custom_category) return true;
    if (!project.project_start_date) return false;
    const endDate = project.project_revised_end_date || project.project_planned_end_date;
    return today >= project.project_start_date && (!endDate || today <= endDate);
  });

  // Format date for display
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase() + ' ' + date.getDate();
  };

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Get ISO week number
  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  // Responsive styles using explicit media queries to split 'lg' range
  const responsiveFontSize = {
    fontSize: '0.875rem',
    '@media (min-width: 768px) and (max-width: 1599px)': {
      fontSize: '0.8125rem',
    },
  };

  const responsiveColWidth = {
    minWidth: 200,
    maxWidth: 250,
    padding: '4px',
    '@media (max-width: 767px)': {
      minWidth: 150,
      maxWidth: 200,
      padding: '4px',
    },
    '@media (min-width: 768px) and (max-width: 1599px)': {
      minWidth: 110,
      maxWidth: 140,
      padding: '2px 4px',
    },
    ...responsiveFontSize
  };

  const responsiveInputWidth = {
    ...responsiveFontSize
  };

  const responsiveDayCell = {
    minWidth: 80,
    padding: '4px',
    '@media (max-width: 767px)': {
      minWidth: 60,
    },
    '@media (min-width: 768px) and (max-width: 1599px)': {
      minWidth: 40,
      padding: '1px',
    },
  };

  const responsiveTotalCell = {
    minWidth: 100,
    fontWeight: 'bold',
    padding: '4px',
    '@media (max-width: 767px)': {
      minWidth: 70,
    },
    '@media (min-width: 768px) and (max-width: 1599px)': {
      minWidth: 50,
      padding: '2px',
    },
    ...responsiveFontSize
  };

  // Compact styles for TS Delays and Validated TS tables
  const compactTableCell = {
    padding: '8px 12px',
    fontSize: '0.875rem',
    '@media (max-width: 767px)': {
      padding: '4px 8px',
      fontSize: '0.8rem',
    },
  };

  const compactTableHeadCell = {
    ...compactTableCell,
    fontWeight: 'bold',
  };

  // Load initial data
  useEffect(() => {
    loadData();
  }, [currentWeekStart, activeTabKey, selectedUserId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const year = currentWeekStart.getFullYear();

      // Load validation data if user is PM/Admin and on validation tab
      if (isPMOrAdmin && activeTabKey === 'delays') {
        const lateUsersResult = await timesheetService.getLateUsersConsolidated();
        setLateUsers(lateUsersResult.late_users);
        setError(null);
      } else if (isPMOrAdmin && activeTabKey === 'submitted') {
        const validationDataResult = await timesheetService.getTimesheetsForValidation(formatDateForAPI(currentWeekStart));
        setValidationData(validationDataResult);
        setModifiedHours({});
        setError(null);
      } else if (activeTabKey === 'categories' || activeTabKey === 'reporting') {
        // Managed by child components — skip full-page reload
        setError(null);
        setLoading(false);
        return;
      } else {
        // Load regular timesheet data

        // Determine target user ID (current user or selected user for PM/Admin)
        // Only pass targetId if it's different from current user to avoid unnecessary permission checks
        const isDifferentUser = isPMOrAdmin && selectedUserId && selectedUserId !== user?.id;
        const targetId = isDifferentUser ? selectedUserId : undefined;
        // Use user?.id for calls that need to be scoped to current user if targetId is undefined
        const effectiveId = targetId || user?.id;

        const [projectsData, clientsData, timesheetData, summaryData, holidaysData, timeOffData, userPermissionsData, myCategories] = await Promise.all([
          timesheetService.getTimesheetProjects(targetId),
          projectsService.getClients(),
          timesheetService.getTimesheet(formatDateForAPI(currentWeekStart), targetId),
          timesheetService.getTimesheetSummary(formatDateForAPI(currentWeekStart), targetId),
          timeOffService.getPublicHolidays(year, user?.user_office?.toString()),
          (user && effectiveId) ? timeOffService.getTimeOffRequestsByUser(effectiveId) : Promise.resolve([]),
          projectsService.getUserPermissions(targetId),
          timesheetCategoriesService.mine(targetId).catch(() => []),
        ]);

        // Merge custom-category WP/WU trees into the maps the Autocompletes already read
        const mergedWorkPackages = { ...{} as { [projectId: number]: WorkPackage[] } };
        const mergedWorkUnits = { ...{} as { [projectId: number]: WorkUnit[] } };
        const mergedPermissions = {
          work_packages: { ...(userPermissionsData?.work_packages || {}) },
          work_units: { ...(userPermissionsData?.work_units || {}) },
        };

        myCategories.forEach((cat) => {
          const projectId = cat.project_id;
          const wps: WorkPackage[] = (cat.subcategories || []).map((s) => ({
            id: s.id!,
            code: s.code || s.name,
            title: s.name,
            workUnits: (s.subcategories2 || []).map((s2) => ({
              id: s2.id!,
              code: s2.code || s2.name,
              title: s2.name,
              work_package_id: s.id,
            })),
          }));
          const wus: WorkUnit[] = wps.flatMap((wp) =>
            (wp.workUnits || []).map((wu) => ({ ...wu, work_package_id: wp.id }))
          );
          mergedWorkPackages[projectId] = wps;
          mergedWorkUnits[projectId] = wus;
          mergedPermissions.work_packages[projectId] = wps.map((wp) => wp.id);
          mergedPermissions.work_units[projectId] = wus.map((wu) => wu.id!);
        });

        // Ensure projects from /projects already include is_custom_category flag;
        // also mark any category projects that came only via /mine
        const categoryProjectIds = new Set(myCategories.map((c) => c.project_id));
        const projectsWithFlags = projectsData.map((p) =>
          categoryProjectIds.has(p.id) || p.is_custom_category
            ? { ...p, is_custom_category: true }
            : p
        );

        setProjects(projectsWithFlags);
        setClients(clientsData);
        setSummary(summaryData);
        setPublicHolidays(holidaysData);
        setTimeOffRequests(timeOffData);
        setUserPermissions(mergedPermissions);
        setWorkPackages((prev) => ({ ...prev, ...mergedWorkPackages }));
        setWorkUnits((prev) => ({ ...prev, ...mergedWorkUnits }));

        // Load project details (WPs and WUs) only for projects present in the timesheet
        const projectIdsInTimesheet = new Set<number>();
        Object.values(timesheetData.entries).forEach((entries: any) => {
          entries.forEach((entry: any) => projectIdsInTimesheet.add(entry.project_id));
        });

        // Load details for non-category projects if not already loaded
        await Promise.all(
          Array.from(projectIdsInTimesheet)
            .filter((id) => !categoryProjectIds.has(id))
            .map((id) => loadProjectDetails(id))
        );

        // Convert API data to rows format
        const loadedRows: TimesheetRow[] = [];
        Object.entries(timesheetData.entries).forEach(([date, entries]) => {
          (entries as any[]).forEach(entry => {
            // For absence entries, group by project/WP/WU like regular entries
            if (entry.isAbsence) {
              // Find existing absence row with same project/WP/WU
              let existingAbsenceRow = loadedRows.find(row =>
                row.isAbsence &&
                row.project_id === entry.project_id &&
                row.work_package_id === entry.work_package_id &&
                row.work_unit_id === entry.work_unit_id
              );

              if (!existingAbsenceRow) {
                existingAbsenceRow = {
                  id: `absence-${entry.project_id}-${entry.work_package_id}-${entry.work_unit_id}`,
                  project_id: entry.project_id,
                  work_package_id: entry.work_package_id,
                  work_unit_id: entry.work_unit_id,
                  work_package_code: entry.work_package_code, // Store codes from backend
                  work_unit_code: entry.work_unit_code, // Store codes from backend
                  hours: {},
                  validated: {},
                  submitted_for_validation: {},
                  isAbsence: true,
                  description: entry.description
                };
                loadedRows.push(existingAbsenceRow);
              }

              existingAbsenceRow.hours[date] = entry.hours;
              existingAbsenceRow.validated[date] = entry.validated || false;
              existingAbsenceRow.submitted_for_validation[date] = entry.submitted_for_validation || false;
              return;
            }

            // Find existing row or create new one for regular entries
            let existingRow = loadedRows.find(row =>
              !row.isAbsence &&
              row.project_id === entry.project_id &&
              row.work_package_id === entry.work_package_id &&
              row.work_unit_id === entry.work_unit_id
            );

            if (!existingRow) {
              existingRow = {
                id: `row-${Date.now()}-${loadedRows.length}`,
                project_id: entry.project_id,
                work_package_id: entry.work_package_id,
                work_unit_id: entry.work_unit_id,
                hours: {},
                validated: {},
                submitted_for_validation: {}
              };
              loadedRows.push(existingRow);
            }

            existingRow.hours[date] = entry.hours;
            existingRow.validated[date] = entry.validated || false;
            existingRow.submitted_for_validation[date] = entry.submitted_for_validation || false;
          });
        });

        // If no regular rows loaded, initialize with an empty row
        // (absence rows do not count as regular rows)
        const regularRows = loadedRows.filter(row => !row.isAbsence);
        if (regularRows.length === 0) {
          loadedRows.push({
            id: `row-${Date.now()}`,
            project_id: 0,
            hours: {},
            validated: {},
            submitted_for_validation: {}
          });
        }

        setRows(loadedRows);

        // Check if timesheet is already submitted
        const submitted = loadedRows.some(row => Object.values(row.submitted_for_validation).some(val => val));
        setIsSubmitted(submitted);

        setError(null);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };


  const addRow = () => {
    // Check if the last row is empty (no project or hours)
    const lastRow = rows[rows.length - 1];
    if (lastRow && lastRow.project_id === 0 && Object.keys(lastRow.hours).length === 0) {
      // Don't add a new row if the last one is empty
      return;
    }

    const newRow: TimesheetRow = {
      id: `row-${Date.now()}`,
      project_id: 0,
      work_package_id: undefined,
      work_unit_id: undefined,
      hours: {},
      validated: {},
      submitted_for_validation: {},
    };
    setRows([...rows, newRow]);
  };

  const removeRow = (rowId: string) => {
    setRows(rows.filter(r => r.id !== rowId));
  };

  const updateRow = (rowId: string, field: keyof TimesheetRow, value: any) => {
    setRows(rows.map(row => {
      if (row.id === rowId) {
        const updatedRow = { ...row, [field]: value };
        // Clear work package and unit when project changes
        if (field === 'project_id') {
          updatedRow.work_package_id = undefined;
          updatedRow.work_unit_id = undefined;

          // Load details for the newly selected project
          if (value > 0) {
            loadProjectDetails(value);
          }
        }
        // Clear work unit when work package changes
        else if (field === 'work_package_id') {
          updatedRow.work_unit_id = undefined;
        }
        return updatedRow;
      }
      return row;
    }));
  };

  const updateHours = (rowId: string, date: string, hours: number) => {
    const row = rows.find(r => r.id === rowId);
    if (!row) return;

    // Prevent entering hours on public holidays
    if (isPublicHoliday(date)) {
      setError('Cannot enter time on public holidays');
      return;
    }

    // Determine max allowed hours for the day
    let maxDailyHours = 24;

    // Enforce max hours per entry based on daily limit
    hours = Math.min(Math.max(hours, 0), maxDailyHours);

    // Calculate what the daily total would be with this change
    const currentDayTotal = rows.reduce((sum, r) => {
      if (r.id === rowId) return sum; // Exclude current row from calculation
      return sum + (r.hours[date] || 0);
    }, 0);

    const newDayTotal = currentDayTotal + hours;

    // Prevent daily total from exceeding max allowed hours
    if (newDayTotal > maxDailyHours) {
      const maxAllowedHours = maxDailyHours - currentDayTotal;
      if (maxAllowedHours <= 0) {
        setError(`Cannot add more hours to this day. Daily total is already at ${maxDailyHours} hours.`);
        return;
      }
      setError(`Cannot add ${hours} hours. Maximum ${maxAllowedHours.toFixed(1)} hours available for this day (${maxDailyHours}h limit).`);
      return;
    }

    // Clear any previous error
    setError(null);

    // Update local state only
    setRows(rows.map(r => {
      if (r.id === rowId) {
        const newHours = { ...r.hours };
        const newModifiedFields = { ...r.modifiedFields };
        if (hours > 0) {
          newHours[date] = hours;
        } else {
          delete newHours[date];
        }
        // Mark this field as modified if it was pre-filled
        if (r.isPrefilled) {
          newModifiedFields[date] = true;
        }
        return { ...r, hours: newHours, modifiedFields: newModifiedFields };
      }
      return r;
    }));
  };

  const calculateRowTotal = (row: TimesheetRow): number => {
    return Object.values(row.hours).reduce((sum, hours) => sum + hours, 0);
  };

  const calculateDayTotal = (date: string): number => {
    return rows.reduce((sum, row) => sum + (row.hours[date] || 0), 0);
  };

  const calculateGrandTotal = (): number => {
    return rows.reduce((sum, row) => sum + calculateRowTotal(row), 0);
  };

  const contractedHours = summary?.weekly_hours ?? user?.weekly_hours ?? 40;

  const isRowSubmitted = (row: TimesheetRow): boolean => {
    return Object.values(row.submitted_for_validation).some(submitted => submitted);
  };

  const hasSubmittedEntries = (): boolean => {
    return rows.some(row => isRowSubmitted(row));
  };

  // Format decimal hours to HH:MM format
  const formatHoursAsTime = (hours: number): string => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Check if a date is a public holiday
  const isPublicHoliday = (dateStr: string): PublicHoliday | null => {
    return publicHolidays.find(holiday => holiday.date === dateStr) || null;
  };

  // Check if a date is a time off request
  const isTimeOff = (dateStr: string): TimeOffRequest | null => {
    return timeOffRequests.find(req => req.date === dateStr && req.status === 'approved') || null;
  };

  const saveTimesheet = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Validate rows - exclude absence rows (auto-generated, not saved to DB)
      const validRows = rows.filter(row =>
        !row.isAbsence &&
        row.project_id > 0 &&
        Object.keys(row.hours).length > 0
      );

      if (validRows.length === 0) {
        setError('Please add at least one timesheet entry with a project and hours');
        return;
      }

      // Convert rows to API format
      const entries: SaveTimesheetEntry[] = [];
      validRows.forEach(row => {
        Object.entries(row.hours).forEach(([date, hours]) => {
          if (hours > 0) {
            entries.push({
              date,
              project_id: row.project_id,
              work_package_id: row.work_package_id,
              work_unit_id: row.work_unit_id,
              hours,
              description: '' // Could be added later if needed
            });
          }
        });
      });

      const isDifferentUser = isPMOrAdmin && selectedUserId && selectedUserId !== user?.id;
      const targetId = isDifferentUser ? selectedUserId : undefined;
      await timesheetService.saveTimesheet(formatDateForAPI(currentWeekStart), entries, targetId);

      setSuccess('Timesheet saved successfully!');
      setTimeout(() => setSuccess(null), 8000);

      // Reset prefill state after save
      setIsPrefilled(false);
      setPrefilledFromPreviousWeek(false);

      // Clear modified fields after save
      setRows(rows.map(r => ({ ...r, modifiedFields: {} })));

      // Reload data to get updated entries
      await loadData();

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save timesheet');
      setTimeout(() => setError(null), 8000);
      console.error('Error saving timesheet:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitClick = () => {
    setSubmitComment("");
    setConfirmSubmitOpen(true);
  };

  const submitTimesheet = async () => {
    try {
      setConfirmSubmitOpen(false);
      setSaving(true);
      setError(null);
      setSuccess(null);

      // First save the current state
      await saveTimesheet();

      const isDifferentUser = isPMOrAdmin && selectedUserId && selectedUserId !== user?.id;
      const targetId = isDifferentUser ? selectedUserId : undefined;

      await timesheetService.submitTimesheetForValidation(formatDateForAPI(currentWeekStart), targetId, submitComment);

      setSuccess('Timesheet submitted for validation successfully!');
      setTimeout(() => setSuccess(null), 8000);

      // Reload data to update status
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit timesheet');
      setTimeout(() => setError(null), 8000);
      console.error('Error submitting timesheet:', err);
    } finally {
      setSaving(false);
    }
  };

  const prefillFromPreviousWeek = async (silent: boolean = false) => {
    try {
      if (!silent) setError(null);
      const prevWeekStart = new Date(currentWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
      const isDifferentUser = isPMOrAdmin && selectedUserId && selectedUserId !== user?.id;
      const targetId = isDifferentUser ? selectedUserId : undefined;
      const prevTimesheetData = await timesheetService.getTimesheet(formatDateForAPI(prevWeekStart), targetId);

      // Convert API data to rows format, shifted to current week
      const prefilledRows: TimesheetRow[] = [];
      Object.entries(prevTimesheetData.entries).forEach(([date, entries]) => {
        (entries as TimesheetEntry[]).forEach(entry => {
          // Find existing row or create new one
          let existingRow = prefilledRows.find(row =>
            row.project_id === entry.project_id &&
            row.work_package_id === entry.work_package_id &&
            row.work_unit_id === entry.work_unit_id
          );

          if (!existingRow) {
            existingRow = {
              id: `row-${Date.now()}-${prefilledRows.length}`,
              project_id: entry.project_id,
              work_package_id: entry.work_package_id,
              work_unit_id: entry.work_unit_id,
              hours: {},
              validated: {},
              submitted_for_validation: {},
              isPrefilled: true,
              modifiedFields: {}
            };
            prefilledRows.push(existingRow);
          }

          // Shift date to current week
          const prevDate = new Date(date);
          const currentDate = new Date(prevDate.getTime() + 7 * 24 * 60 * 60 * 1000);
          const currentDateStr = formatDateForAPI(currentDate);

          existingRow.hours[currentDateStr] = entry.hours;
        });
      });

      if (prefilledRows.length > 0) {
        // Preserve existing absence rows when prefilling
        const existingAbsenceRows = rows.filter(row => row.isAbsence);
        setRows([...existingAbsenceRows, ...prefilledRows]);
        setIsPrefilled(true);
        setPrefilledFromPreviousWeek(true);
        if (!silent) {
          setSuccess('Timesheet prefilled from previous week');
          setTimeout(() => setSuccess(null), 5000);
        }
      } else {
        if (!silent) {
          setError('No data found in previous week');
          setTimeout(() => setError(null), 5000);
        }
      }
    } catch (err: any) {
      if (!silent) {
        setError('Failed to prefill from previous week');
        setTimeout(() => setError(null), 5000);
      }
      console.error('Error prefilling from previous week:', err);
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(newDate);
    // Reset prefill state when navigating weeks
    setIsPrefilled(false);
    setPrefilledFromPreviousWeek(false);
  };



  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  return (
    <Box>

      {/* Tabs when more than one is available */}
      {tabs.length > 1 && (
        <Box sx={{ borderBottom: `1px solid ${panelBorder}`, mb: 3 }}>
          <Tabs
            value={activeTabIndex}
            onChange={(_, newValue) => setActiveTabKey(tabs[newValue].key)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontWeight: 600,
                fontSize: '0.75rem',
                minHeight: 42,
                color: isDark ? '#b0bec5' : '#546e7a',
              },
              '& .Mui-selected': {
                color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean,
              },
            }}
          >
            {tabs.map((tab) => (
              <Tab key={tab.key} label={tab.label} />
            ))}
          </Tabs>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1, border: `1px solid ${panelBorder}` }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 1, border: `1px solid ${panelBorder}` }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tab Content */}
      {activeTabKey === 'reporting' ? (
        <TimesheetReportingPage />
      ) : activeTabKey === 'categories' ? (
        <CustomCategoriesTab />
      ) : activeTabKey === 'mine' ? (
        // Timesheet Entry Tab
        <>
          {/* External User Selector */}
          {isPMOrAdmin && externalUsers.length > 0 && (
            <Box display="flex" justifyContent="center" mb={2}>
              <Autocomplete
                sx={{ minWidth: 250 }}
                size="small"
                options={[
                  { id: user?.id, name: `Myself (${user?.name})` },
                  ...externalUsers.map(u => ({ id: u.id, name: `${u.name} (External)` }))
                ]}
                getOptionLabel={(option) => option.name}
                value={
                  selectedUserId === user?.id
                    ? { id: user?.id, name: `Myself (${user?.name})` }
                    : externalUsers.find(u => u.id === selectedUserId)
                      ? { id: selectedUserId!, name: `${externalUsers.find(u => u.id === selectedUserId)?.name} (External)` }
                      : null
                }
                onChange={(_, newValue) => {
                  if (newValue) {
                    handleUserChange({ target: { value: newValue.id } });
                  }
                }}
                renderInput={(params) => <TextField {...params} label="Timesheet User" />}
                slotProps={getZoomCompensatedAutocompleteSlotProps()}
              />
            </Box>
          )}

          {/* Week Navigation */}
          <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
            <IconButton onClick={() => navigateWeek('prev')} sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
              <NavigateBefore />
            </IconButton>
            <Typography variant="h6" sx={{ mx: 3, fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>
              Week {getWeekNumber(weekDates[0])} ({weekDates[0].toLocaleDateString('en-US', { month: 'long' })}) of {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
            </Typography>
            <IconButton onClick={() => navigateWeek('next')} sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
              <NavigateNext />
            </IconButton>
          </Box>



          {isMobile ? (
            // Mobile layout
            <Box sx={{ pb: 10 }}>
              {rows.map((row) => (
                <Accordion key={row.id} sx={{ mb: 1, borderRadius: '4px !important', boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg, '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }} />}>
                    <Typography>
                      {projects.find(p => p.id === row.project_id)?.name || 'Select Project'} - {calculateRowTotal(row).toFixed(1)}h
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ mb: 2 }}>
                      <Autocomplete
                        options={filteredProjects}
                        getOptionLabel={(project) => {
                          if (project.is_custom_category) return project.name;
                          const client = clients.find(c => c.id === project.client_id);
                          return (client?.abbreviation ? client.abbreviation + ' - ' : '') + project.name;
                        }}
                        value={filteredProjects.find(p => p.id === row.project_id) || null}
                        onChange={(_, newValue) => updateRow(row.id, 'project_id', newValue?.id || 0)}
                        disabled={isRowSubmitted(row)}
                        slotProps={getZoomCompensatedAutocompleteSlotProps()}
                        renderInput={(params) => <TextField {...params} label="Select Project" size="small" sx={{ mb: 1 }} />}
                      />
                      <Autocomplete
                        options={(workPackages[row.project_id] || []).filter(wp => (userPermissions?.work_packages[row.project_id] || []).includes(wp.id))}
                        getOptionLabel={(wp) => formatTimesheetCodeTitleLabel(wp)}
                        value={(workPackages[row.project_id] || []).find(wp => wp.id === row.work_package_id) || null}
                        onChange={(_, newValue) => updateRow(row.id, 'work_package_id', newValue?.id)}
                        disabled={!row.project_id || isRowSubmitted(row)}
                        slotProps={getZoomCompensatedAutocompleteSlotProps()}
                        renderInput={(params) => <TextField {...params} label="Select Work Package" size="small" sx={{ mb: 1 }} />}
                      />
                      <Autocomplete
                        options={(workUnits[row.project_id] || [])
                          .filter(wu => !row.work_package_id || wu.work_package_id === row.work_package_id)
                          .filter(wu => (userPermissions?.work_units[row.project_id] || []).includes(wu.id))}
                        getOptionLabel={(wu) => formatTimesheetCodeTitleLabel(wu)}
                        value={(workUnits[row.project_id] || []).find(wu => wu.id === row.work_unit_id) || null}
                        onChange={(_, newValue) => updateRow(row.id, 'work_unit_id', newValue?.id)}
                        disabled={!row.project_id || isRowSubmitted(row)}
                        slotProps={getZoomCompensatedAutocompleteSlotProps()}
                        renderInput={(params) => <TextField {...params} label="Select Work Unit" size="small" sx={{ mb: 1 }} />}
                      />
                    </Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Hours per day:
                      {row.isPrefilled && (
                        <Chip
                          label="Draft - Save to persist changes"
                          size="small"
                          color="warning"
                          sx={{ ml: 1, fontSize: '0.7rem' }}
                        />
                      )}
                    </Typography>
                    {weekDates.map((date, index) => {
                      const dateStr = formatDateForAPI(date);
                      const holiday = isPublicHoliday(dateStr);
                      const timeOff = isTimeOff(dateStr);
                      const isBlocked = !!holiday;
                      const isHalfDay = timeOff?.holidayType === 'half_day';

                      let tooltipTitle = '';
                      if (holiday) {
                        tooltipTitle = holiday.name;
                      } else if (timeOff) {
                        tooltipTitle = `Warning: You have approved time off on this day. Time Off: ${timeOff.hrCategory}`;
                        if (isHalfDay) {
                          tooltipTitle += ` (${timeOff.halfDayPeriod})`;
                        }
                      }

                      return (
                        <Grid container key={index} spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Grid item xs={6}>
                            <Tooltip title={tooltipTitle}>
                              <Box sx={{ display: 'flex', alignItems: 'center', color: isBlocked ? 'error.main' : 'inherit', fontWeight: isBlocked ? 'bold' : 'normal' }}>
                                <Typography>{formatDate(date)}</Typography>
                                {timeOff && timeOff.holidayType !== 'remote' && (
                                  <WarningAmberIcon fontSize="small" color="warning" sx={{ ml: 0.5 }} />
                                )}
                              </Box>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={4}>
                            <Tooltip title={isBlocked ? `${tooltipTitle} - Time entry disabled` : (isSubmitted ? 'Timesheet submitted - cannot edit' : (row.submitted_for_validation[dateStr] ? 'Entry submitted for validation - cannot edit' : tooltipTitle))}>
                              <TextField
                                key={`${row.id}-${dateStr}-${row.modifiedFields?.[dateStr] || false}`}
                                type="number"
                                size="small"
                                value={row.hours[dateStr] || ''}
                                onChange={(e) => updateHours(row.id, dateStr, parseFloat(e.target.value) || 0)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && index === 4) {
                                    saveTimesheet();
                                  }
                                }}
                                disabled={isBlocked || isSubmitted}
                                inputProps={{ min: 0, max: 24, step: 0.5, style: { textAlign: 'center' } }}
                                sx={{
                                  width: '100%',
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: (row.isPrefilled && !row.modifiedFields?.[dateStr]) ? '#ccba9b' : 'inherit',
                                  },
                                }}
                              />
                            </Tooltip>
                          </Grid>
                          <Grid item xs={2}>
                            <Tooltip title={isRowSubmitted(row) ? "Cannot delete - row contains submitted entries" : "Delete row"}>
                              <IconButton size="small" onClick={() => removeRow(row.id)} color="error" disabled={isRowSubmitted(row)}>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
              <Box mt={2} display="flex" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addRow}
                  disabled={isSubmitted}
                  sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.75rem' }}
                >
                  Add Row
                </Button>
                {!isPrefilled && (
                  <Button
                    variant="outlined"
                    onClick={() => prefillFromPreviousWeek(false)}
                    disabled={isSubmitted || loading}
                    sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.75rem' }}
                  >
                    Pre-fill from Previous Week
                  </Button>
                )}
              </Box>
              {/* Totals */}
              <Box sx={{ mt: 3, p: 2, borderRadius: 1, border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
                <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>Daily Totals</Typography>
                {weekDates.map((date, index) => {
                  const dateStr = formatDateForAPI(date);
                  const total = calculateDayTotal(dateStr);
                  const holiday = isPublicHoliday(dateStr);
                  const timeOff = isTimeOff(dateStr);
                  const isBlocked = !!holiday;
                  const isHalfDay = timeOff?.holidayType === 'half_day';

                  let tooltipTitle = '';
                  if (holiday) {
                    tooltipTitle = holiday.name;
                  } else if (timeOff) {
                    tooltipTitle = `Warning: You have approved time off on this day. Time Off: ${timeOff.hrCategory}`;
                    if (isHalfDay) {
                      tooltipTitle += ` (${timeOff.halfDayPeriod})`;
                    }
                  }

                  return (
                    <Box key={index} display="flex" justifyContent="space-between" sx={{ my: 0.5 }}>
                      <Tooltip title={tooltipTitle}>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: isBlocked ? 'error.main' : 'inherit', fontWeight: isBlocked ? 'bold' : 'normal' }}>
                          <Typography>{formatDate(date)}</Typography>
                          {timeOff && timeOff.holidayType !== 'remote' && (
                            <WarningAmberIcon fontSize="small" color="warning" sx={{ ml: 0.5 }} />
                          )}
                        </Box>
                      </Tooltip>
                      <Typography>{total > 0 ? `${total.toFixed(1)}h` : '-'}</Typography>
                    </Box>
                  );
                })}
                <Box display="flex" justifyContent="space-between" sx={{ mt: 1, fontWeight: 'bold' }}>
                  <Typography variant="h6">Grand Total</Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: Math.abs(calculateGrandTotal() - contractedHours) < 0.01
                        ? 'inherit'
                        : calculateGrandTotal() < contractedHours
                          ? 'warning.main'
                          : 'info.main'
                    }}
                  >
                    {calculateGrandTotal().toFixed(1)} / {contractedHours}h
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            // Desktop layout
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={responsiveColWidth}>Project</TableCell>
                      <TableCell sx={responsiveColWidth}>Work Package</TableCell>
                      <TableCell sx={responsiveColWidth}>Work Unit</TableCell>
                      {weekDates.map((date, index) => {
                        const dateStr = formatDateForAPI(date);
                        const holiday = isPublicHoliday(dateStr);
                        const timeOff = isTimeOff(dateStr);
                        const isBlocked = !!holiday;
                        const isHalfDay = timeOff?.holidayType === 'half_day';

                        let tooltipTitle = '';
                        if (holiday) {
                          tooltipTitle = holiday.name;
                        } else if (timeOff) {
                          tooltipTitle = `Warning: You have approved time off on this day. Time Off: ${timeOff.hrCategory}`;
                          if (isHalfDay) {
                            tooltipTitle += ` (${timeOff.halfDayPeriod})`;
                          }
                        }

                        return (
                          <TableCell key={index} align="center" sx={responsiveDayCell}>
                            <Tooltip title={tooltipTitle}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: isBlocked ? 'error.main' : 'inherit', fontWeight: isBlocked ? 'bold' : 'normal', ...responsiveFontSize }}>
                                {formatDate(date)}
                                {timeOff && timeOff.holidayType !== 'remote' && (
                                  <WarningAmberIcon fontSize="small" color="warning" sx={{ ml: 0.5, fontSize: '0.9rem' }} />
                                )}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                      <TableCell align="center" sx={responsiveTotalCell}>
                        Total
                      </TableCell>
                      <TableCell align="center" sx={{ width: 60, padding: '4px', ...responsiveFontSize }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={row.isAbsence ? {
                          bgcolor: 'rgba(255, 152, 0, 0.15)',
                          '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.25)' }
                        } : undefined}
                      >
                        <TableCell sx={responsiveColWidth}>
                          {row.isAbsence ? (
                            <Tooltip title="Auto-generated absence entry - cannot modify">
                              <Box sx={{ color: 'warning.main', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} />
                                {filteredProjects.find(p => p.id === row.project_id)?.name || 'Absence'}
                              </Box>
                            </Tooltip>
                          ) : (
                            <Autocomplete
                              options={filteredProjects}
                              getOptionLabel={(project) => {
                                if (project.is_custom_category) return project.name;
                                const client = clients.find(c => c.id === project.client_id);
                                return (client?.abbreviation ? client.abbreviation + ' - ' : '') + project.name;
                              }}
                              value={filteredProjects.find(p => p.id === row.project_id) || null}
                              onChange={(_, newValue) => updateRow(row.id, 'project_id', newValue?.id || 0)}
                              disabled={isRowSubmitted(row)}
                              slotProps={getZoomCompensatedAutocompleteSlotProps()}
                              renderInput={(params) => <TextField {...params} size="small" sx={responsiveFontSize} />}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={responsiveColWidth}>
                          {row.isAbsence ? (
                            <Typography sx={{ ...responsiveFontSize, color: 'warning.main' }}>
                              {row.work_package_code || '-'}
                            </Typography>
                          ) : (
                            <Autocomplete
                              options={(workPackages[row.project_id] || []).filter(wp => (userPermissions?.work_packages[row.project_id] || []).includes(wp.id))}
                              getOptionLabel={(wp) => formatTimesheetCodeTitleLabel(wp)}
                              value={(workPackages[row.project_id] || []).find(wp => wp.id === row.work_package_id) || null}
                              onChange={(_, newValue) => updateRow(row.id, 'work_package_id', newValue?.id)}
                              disabled={!row.project_id || isRowSubmitted(row)}
                              slotProps={getZoomCompensatedAutocompleteSlotProps()}
                              renderInput={(params) => <TextField {...params} size="small" sx={responsiveFontSize} />}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={responsiveColWidth}>
                          {row.isAbsence ? (
                            <Typography sx={{ ...responsiveFontSize, color: 'warning.main' }}>
                              {row.work_unit_code || '-'}
                            </Typography>
                          ) : (
                            <Autocomplete
                              options={(workUnits[row.project_id] || [])
                                .filter(wu => !row.work_package_id || wu.work_package_id === row.work_package_id)
                                .filter(wu => (userPermissions?.work_units[row.project_id] || []).includes(wu.id))}
                              getOptionLabel={(wu) => formatTimesheetCodeTitleLabel(wu)}
                              value={(workUnits[row.project_id] || []).find(wu => wu.id === row.work_unit_id) || null}
                              onChange={(_, newValue) => updateRow(row.id, 'work_unit_id', newValue?.id)}
                              disabled={!row.project_id || isRowSubmitted(row)}
                              slotProps={getZoomCompensatedAutocompleteSlotProps()}
                              renderInput={(params) => <TextField {...params} size="small" sx={responsiveFontSize} />}
                            />
                          )}
                        </TableCell>
                        {weekDates.map((date, index) => {
                          const dateStr = formatDateForAPI(date);
                          const holiday = isPublicHoliday(dateStr);
                          const timeOff = isTimeOff(dateStr);
                          const isBlocked = !!holiday;
                          const isHalfDay = timeOff?.holidayType === 'half_day';

                          let tooltipTitle = '';
                          if (holiday) {
                            tooltipTitle = holiday.name;
                          } else if (timeOff) {
                            tooltipTitle = `Warning: You have approved time off on this day. Time Off: ${timeOff.hrCategory}`;
                            if (isHalfDay) {
                              tooltipTitle += ` (${timeOff.halfDayPeriod})`;
                            }
                          }

                          return (
                            <TableCell key={index} align="center" sx={responsiveDayCell}>
                              <Tooltip title={isBlocked ? `${tooltipTitle} - Time entry disabled` : (isSubmitted ? 'Timesheet submitted - cannot edit' : (row.submitted_for_validation[dateStr] ? 'Entry submitted for validation - cannot edit' : tooltipTitle))}>
                                <TextField
                                  type="number"
                                  size="small"
                                  value={row.hours[dateStr] || ''}
                                  onChange={(e) => updateHours(row.id, dateStr, parseFloat(e.target.value) || 0)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && index === 4) {
                                      saveTimesheet();
                                    }
                                  }}
                                  disabled={isBlocked || isSubmitted || row.isAbsence}
                                  inputProps={{
                                    min: 0,
                                    max: 24,
                                    step: 0.5,
                                    style: { textAlign: 'center', padding: '4px 2px' }
                                  }}
                                  sx={{
                                    width: 60,
                                    '@media (max-width: 767px)': { width: 70 },
                                    '@media (min-width: 768px) and (max-width: 1599px)': { width: 45 },
                                    ...responsiveFontSize,
                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                      '-webkit-appearance': 'none',
                                      margin: 0,
                                      display: 'none',
                                    },
                                    '& input[type=number]': {
                                      '-moz-appearance': 'textfield',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: (row.isPrefilled && !row.modifiedFields?.[dateStr]) ? '#ccba9b' : 'inherit',
                                    },
                                  }}
                                  InputProps={{ sx: responsiveInputWidth }}
                                />
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={responsiveTotalCell}>
                          {calculateRowTotal(row).toFixed(1)}h
                        </TableCell>
                        <TableCell align="center" sx={{ padding: '4px' }}>
                          {row.isAbsence ? (
                            <Tooltip title="Auto-generated absence entry - cannot delete">
                              <Box sx={{ color: 'warning.main' }}>
                                <WarningAmberIcon fontSize="small" />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Tooltip title={isRowSubmitted(row) ? "Cannot delete - row contains submitted entries" : "Delete row"}>
                              <IconButton
                                size="small"
                                onClick={() => removeRow(row.id)}
                                color="error"
                                disabled={isRowSubmitted(row)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Totals Row */}
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell colSpan={3} sx={{ fontWeight: 'bold', padding: '4px', ...responsiveFontSize }}>
                        Daily Totals
                      </TableCell>
                      {weekDates.map((date, index) => {
                        const dateStr = formatDateForAPI(date);
                        const total = calculateDayTotal(dateStr);
                        const holiday = isPublicHoliday(dateStr);
                        const timeOff = isTimeOff(dateStr);
                        const isBlocked = !!holiday;
                        const isHalfDay = timeOff?.holidayType === 'half_day';

                        return (
                          <TableCell key={index} align="center" sx={{ fontWeight: 'bold', padding: '4px', color: isBlocked ? 'error.main' : 'inherit', ...responsiveFontSize }}>
                            {total > 0 ? `${total.toFixed(1)}h` : '-'}
                          </TableCell>
                        );
                      })}
                      <TableCell
                        align="center"
                        sx={{
                          ...responsiveTotalCell,
                          color: Math.abs(calculateGrandTotal() - contractedHours) < 0.01
                            ? 'inherit'
                            : calculateGrandTotal() < contractedHours
                              ? 'warning.main'
                              : 'info.main'
                        }}
                      >
                        {calculateGrandTotal().toFixed(1)} / {contractedHours}h
                      </TableCell>
                      <TableCell sx={{ padding: '4px' }} />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Box mt={2} display="flex" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addRow}
                  disabled={isSubmitted}
                  sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.75rem' }}
                >
                  Add Row
                </Button>
                {!isPrefilled && (
                  <Button
                    variant="outlined"
                    onClick={() => prefillFromPreviousWeek(false)}
                    disabled={isSubmitted || loading}
                    sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.75rem' }}
                  >
                    Pre-fill from Previous Week
                  </Button>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveTimesheet}
                  disabled={saving || rows.length === 0 || isSubmitted}
                  sx={{ borderRadius: 1, fontWeight: 600, boxShadow: 'none', bgcolor: isDark ? alpha(NAUTILUS.sand, 0.15) : alpha(NAUTILUS.deepOcean, 0.08), color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, border: `1px solid ${panelBorder}`, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', '&:hover': { boxShadow: 'none', bgcolor: isDark ? alpha(NAUTILUS.sand, 0.25) : alpha(NAUTILUS.deepOcean, 0.15) } }}
                >
                  {saving ? 'Saving...' : 'Save Timesheet'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleSubmitClick}
                  disabled={saving || rows.length === 0 || isSubmitted}
                  sx={{ borderRadius: 1, fontWeight: 600, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
                >
                  Submit for Validation
                </Button>
              </Box>
            </>
          )}

          {/* Graph and Summary Section */}
          {!isMobile && rows.length > 0 && (
            <Paper sx={{ p: 3, mt: 3, borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>
                My working hours
              </Typography>

              <Box height={300} width="100%" mt={2}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={weekDates.map(date => {
                      const dateStr = formatDateForAPI(date);
                      const dayData: any = {
                        date: dateStr,
                        displayDate: date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }),
                        total: 0
                      };

                      rows.forEach(row => {
                        const hours = row.hours[dateStr] || 0;
                        if (hours > 0) {
                          const project = projects.find(p => p.id === row.project_id);
                          const projectName = project ? project.name : 'Unknown Project';
                          dayData[projectName] = (dayData[projectName] || 0) + hours;
                          dayData.total += hours;
                        }
                      });
                      return dayData;
                    })}
                    margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? '#2c3e50' : '#eee'} />
                    <XAxis
                      dataKey="displayDate"
                      tick={{ fontSize: 12, fill: isDark ? '#b0bec5' : '#666' }}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      domain={[0, 'auto']}
                      tick={{ fontSize: 12, fill: isDark ? '#b0bec5' : '#666' }}
                    />
                    <RechartsTooltip
                      cursor={{ fill: 'transparent' }}
                      contentStyle={{ borderRadius: 4, border: `1px solid ${panelBorder}`, boxShadow: 'none', backgroundColor: surfaceBg }}
                    />
                    {Array.from(new Set(rows.map(row => {
                      const p = projects.find(proj => proj.id === row.project_id);
                      return p ? p.name : null;
                    }).filter(Boolean))).map((project, index) => (
                      <Bar
                        key={project as string}
                        dataKey={project as string}
                        stackId="a"
                        fill={['#2E86C1', '#17A589', '#F1C40F', '#E67E22', '#8E44AD'][index % 5]}
                        barSize={60}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </Box>

              {/* Summary Stats */}
              <Grid container spacing={2} sx={{ mt: 4, pt: 2, borderTop: `1px solid ${panelBorder}` }}>
                <Grid item xs={3} textAlign="center">
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
                    {(() => {
                      const today = new Date();
                      const todayStr = formatDateForAPI(today);
                      const isTodayInView = weekDates.some(d => formatDateForAPI(d) === todayStr);
                      const val = isTodayInView ? calculateDayTotal(todayStr) : (summary?.today_total || 0);
                      return val.toFixed(2).replace('.', ':'); // Format as HH:MM style roughly, or just decimal
                    })()}
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#b0bec5' : '#546e7a' }}>
                    Today, {new Date().toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={3} textAlign="center">
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
                    {calculateGrandTotal().toFixed(2).replace('.', ':')}
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#b0bec5' : '#546e7a' }}>
                    Calendar week {getWeekNumber(weekDates[0])}
                  </Typography>
                </Grid>
                <Grid item xs={3} textAlign="center">
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
                    {summary?.month_total?.toFixed(2).replace('.', ':') || '0:00'}
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#b0bec5' : '#546e7a' }}>
                    {summary?.current_month_name || 'Month'}
                  </Typography>
                </Grid>
                <Grid item xs={3} textAlign="center">
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'monospace', color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
                    {summary?.year_total?.toFixed(2).replace('.', ':') || '0:00'}
                  </Typography>
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#b0bec5' : '#546e7a' }}>
                    Financial year
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          {isMobile && activeTabKey === 'mine' && (
            <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, bgcolor: surfaceBg, p: 2, borderTop: `1px solid ${panelBorder}`, zIndex: 1000, display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SaveIcon />}
                onClick={saveTimesheet}
                disabled={saving || rows.length === 0 || isSubmitted}
                fullWidth
                sx={{ borderRadius: 1, borderColor: panelBorder, color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, fontSize: '0.75rem' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleSubmitClick}
                disabled={saving || rows.length === 0 || isSubmitted}
                fullWidth
                sx={{ borderRadius: 1, fontWeight: 600, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
              >
                Submit
              </Button>
            </Box>
          )}
        </>
      ) : activeTabKey === 'delays' ? (
        // TS Delays Tab
        <Box>
          {/* Late Users Section */}
          {lateUsers.length > 0 && (
            <Paper sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
              <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', color: isDark ? '#b0bec5' : '#546e7a' }}>
                Users Late with Timesheets
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={compactTableHeadCell}>User</TableCell>
                      <TableCell sx={compactTableHeadCell}>Delayed Weeks</TableCell>
                      <TableCell sx={compactTableHeadCell}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lateUsers.map(user => (
                      <TableRow key={user.id} sx={{ minHeight: '80px' }}>
                        <TableCell sx={compactTableCell}>{user.name}</TableCell>
                        <TableCell sx={compactTableCell}>
                          {user.delayedWeeks?.map(week => {
                            const weekDate = new Date(week);
                            const weekNum = getWeekNumber(weekDate);
                            return `Week ${weekNum} (${weekDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})`;
                          }).join(', ') || 'N/A'}
                        </TableCell>
                        <TableCell sx={compactTableCell}>
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ fontSize: '0.75rem', padding: '2px 6px', borderRadius: 1, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
                            onClick={() => {
                              timesheetService.sendReminders([{ user_id: user.id, delayed_weeks: user.delayedWeeks || [] }]).then(result => {
                                setSuccess(result.message);
                                setTimeout(() => setSuccess(null), 5000);
                              }).catch(err => {
                                setError('Failed to send reminder');
                                setTimeout(() => setError(null), 5000);
                              });
                            }}
                          >
                            Send Reminder
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box mt={2}>
                <Button
                  size="small"
                  variant="contained"
                  sx={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: 1, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
                  onClick={() => {
                    const reminders = lateUsers.map(u => ({ user_id: u.id, delayed_weeks: u.delayedWeeks || [] }));
                    timesheetService.sendReminders(reminders).then(result => {
                      setSuccess(result.message);
                      setTimeout(() => setSuccess(null), 5000);
                    }).catch(err => {
                      setError('Failed to send reminders');
                      setTimeout(() => setError(null), 5000);
                    });
                  }}
                >
                  Send Reminders to All
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      ) : (
        // Validated TS Tab
        <Box>
          {/* Week Navigation */}
          <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
            <IconButton onClick={() => navigateWeek('prev')} sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
              <NavigateBefore />
            </IconButton>
            <Typography variant="h6" sx={{ mx: 3, fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>
              Week {getWeekNumber(weekDates[0])} ({weekDates[0].toLocaleDateString('en-US', { month: 'long' })}) of {formatDate(weekDates[0])} - {formatDate(weekDates[6])}
            </Typography>
            <IconButton onClick={() => navigateWeek('next')} sx={{ color: isDark ? NAUTILUS.sand : NAUTILUS.deepOcean }}>
              <NavigateNext />
            </IconButton>
          </Box>

          {/* Timesheets for Validation */}
          {validationData?.users_timesheets.map(userTimesheet => {
            // Group entries by project/WP/WU combination
            const groupedEntries: { [key: string]: { entries: TimesheetEntryValidation[], project_name: string, work_package_code?: string, work_unit_code?: string } } = {};

            userTimesheet.entries.forEach(entry => {
              const key = `${entry.project_id}-${entry.work_package_id || 'null'}-${entry.work_unit_id || 'null'}`;
              if (!groupedEntries[key]) {
                groupedEntries[key] = {
                  entries: [],
                  project_name: entry.project_name,
                  work_package_code: entry.work_package_code,
                  work_unit_code: entry.work_unit_code
                };
              }
              groupedEntries[key].entries.push(entry);
            });

            return (
              <Paper key={userTimesheet.user_id} sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>
                    {userTimesheet.user_name}'s Timesheet
                  </Typography>
                  {userTimesheet.comment && (
                    <Tooltip title="User Comment">
                      <Chip
                        label={userTimesheet.comment}
                        variant="outlined"
                        sx={{ maxWidth: 300, borderColor: panelBorder, borderRadius: 1 }}
                      />
                    </Tooltip>
                  )}
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={compactTableHeadCell}>Project</TableCell>
                        <TableCell sx={compactTableHeadCell}>Work Package</TableCell>
                        <TableCell sx={compactTableHeadCell}>Work Unit</TableCell>
                        {weekDates.map((date, index) => (
                          <TableCell key={index} align="center" sx={compactTableHeadCell}>
                            {formatDate(date)}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={compactTableHeadCell}>Total</TableCell>
                        <TableCell align="center" sx={compactTableHeadCell}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(groupedEntries).map(([key, group]) => {
                        const template = group.entries[0];
                        const userMods = modifiedHours[userTimesheet.user_id] || {};
                        const totalHours = weekDates.reduce((sum, date) => {
                          const dateStr = formatDateForAPI(date);
                          const entry = group.entries.find(e => e.date === dateStr);
                          const edit = userMods[`${key}-${dateStr}`];
                          const hours = edit !== undefined ? edit.hours : (entry?.hours || 0);
                          return sum + hours;
                        }, 0);
                        return (
                          <TableRow key={key} sx={{ minHeight: '80px' }}>
                            <TableCell sx={compactTableCell}>{group.project_name}</TableCell>
                            <TableCell sx={compactTableCell}>{group.work_package_code || '-'}</TableCell>
                            <TableCell sx={compactTableCell}>{group.work_unit_code || '-'}</TableCell>
                            {weekDates.map((date, index) => {
                              const dateStr = formatDateForAPI(date);
                              const entry = group.entries.find(e => e.date === dateStr);
                              const cellKey = `${key}-${dateStr}`;
                              const edit = userMods[cellKey];
                              const displayHours = edit !== undefined ? edit.hours : entry?.hours;

                              return (
                                <TableCell key={index} align="center" sx={compactTableCell}>
                                  <TextField
                                    type="number"
                                    size="small"
                                    value={displayHours || ''}
                                    onChange={(e) => {
                                      let newHours = parseFloat(e.target.value);
                                      if (e.target.value === '' || Number.isNaN(newHours)) {
                                        newHours = 0;
                                      }
                                      newHours = Math.min(Math.max(newHours, 0), 24);
                                      const originalHours = entry?.hours || 0;
                                      setModifiedHours(prev => {
                                        const nextUserMods = { ...(prev[userTimesheet.user_id] || {}) };
                                        if (Math.abs(newHours - originalHours) < 0.01) {
                                          delete nextUserMods[cellKey];
                                        } else {
                                          nextUserMods[cellKey] = {
                                            entryId: entry?.id,
                                            date: dateStr,
                                            project_id: template.project_id,
                                            work_package_id: template.work_package_id ?? null,
                                            work_unit_id: template.work_unit_id ?? null,
                                            hours: newHours,
                                          };
                                        }
                                        return {
                                          ...prev,
                                          [userTimesheet.user_id]: nextUserMods,
                                        };
                                      });
                                    }}
                                    inputProps={{
                                      min: 0,
                                      max: 24,
                                      step: 0.5,
                                      style: { textAlign: 'center', padding: '4px 8px' }
                                    }}
                                    sx={{
                                      width: 80,
                                      '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                        '-webkit-appearance': 'none',
                                        margin: 0,
                                        display: 'none',
                                      },
                                      '& input[type=number]': {
                                        '-moz-appearance': 'textfield',
                                      },
                                    }}
                                  />
                                </TableCell>
                              );
                            })}
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                              {totalHours}h
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={"Submitted"} color="success" size="small" />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Save Changes Button - only show if there are modified hours for this user */}
                {modifiedHours[userTimesheet.user_id] && Object.keys(modifiedHours[userTimesheet.user_id]).length > 0 && (
                  <Box mt={2}>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        try {
                          setSaving(true);
                          setError(null);

                          const updates = Object.values(modifiedHours[userTimesheet.user_id] || {}).filter(
                            (mod) => mod.entryId || mod.hours > 0
                          );
                          const updatePromises = updates.map((mod) =>
                            timesheetService.upsertSubmittedTimesheetEntry({
                              entry_id: mod.entryId,
                              hours: mod.hours,
                              user_id: userTimesheet.user_id,
                              date: mod.date,
                              project_id: mod.project_id,
                              work_package_id: mod.work_package_id,
                              work_unit_id: mod.work_unit_id,
                            })
                          );

                          await Promise.all(updatePromises);

                          setSuccess(`Successfully updated ${updates.length} timesheet entries for ${userTimesheet.user_name}`);
                          setTimeout(() => setSuccess(null), 5000);

                          // Clear modified hours for this user
                          setModifiedHours(prev => {
                            const newModified = { ...prev };
                            delete newModified[userTimesheet.user_id];
                            return newModified;
                          });

                          // Reload data to get updated entries
                          await loadData();
                        } catch (err: any) {
                          setError(err.response?.data?.error || 'Failed to update timesheet entries');
                          setTimeout(() => setError(null), 5000);
                        } finally {
                          setSaving(false);
                        }
                      }}
                      disabled={saving}
                      sx={{ borderRadius: 1, fontWeight: 600, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.75rem', '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
                    >
                      {saving ? 'Saving...' : `Save Changes for ${userTimesheet.user_name}`}
                    </Button>
                  </Box>
                )}
              </Paper>
            );
          })}

          {validationData && validationData.users_timesheets.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 1, boxShadow: 'none', border: `1px solid ${panelBorder}`, bgcolor: surfaceBg }}>
              <Typography variant="body1" color="text.secondary">
                No submitted timesheets for this week
              </Typography>
            </Paper>
          )}
        </Box>
      )}


      {/* Submit Confirmation Dialog */}
      <Dialog
        open={confirmSubmitOpen}
        onClose={() => setConfirmSubmitOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 1,
            bgcolor: surfaceBg,
            border: `1px solid ${panelBorder}`,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${panelBorder}`, py: 1.5, fontWeight: 600, letterSpacing: '-0.01em', color: isDark ? '#ffffff' : NAUTILUS.deepOcean }}>Submit Timesheet</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to submit your timesheet for validation?
            Once submitted, you will not be able to edit it unless it is rejected by a PM.
          </Typography>
          {Math.abs(calculateGrandTotal() - contractedHours) > 0.01 && (
            <Alert severity="warning" sx={{ my: 2 }}>
              This week totals {calculateGrandTotal().toFixed(1)}h, but the contracted hours are {contractedHours}h/week.
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            id="comment"
            label="Comment (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={submitComment}
            onChange={(e) => setSubmitComment(e.target.value)}
            placeholder="Add a note about this week's work..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${panelBorder}`, gap: 2 }}>
          <Button
            onClick={() => setConfirmSubmitOpen(false)}
            sx={{ borderRadius: 1, px: 3, color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitTimesheet}
            variant="contained"
            sx={{ borderRadius: 1, px: 3, fontWeight: 600, boxShadow: 'none', bgcolor: NAUTILUS.sand, color: NAUTILUS.deepOcean, '&:hover': { bgcolor: alpha(NAUTILUS.sand, 0.85), boxShadow: 'none' } }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimesheetPage;